import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import { useCustomerGetCustomerProfile } from '@eneco/dc/hooks';

import type { PersonalizedTitleTextCTAContent } from './personalized-title-text-cta-content.types';
import { TitleTextCTAView } from './TitleTextCTAView';

/**
 * `PersonalizedTitleTextCTA` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy Sitecore lane
 * (`libs/sitecore/components/content/src/PersonalizedTitleTextCTA/PersonalizedTitleTextCTA.tsx`), which
 * is itself a thin wrapper around `TitleTextCTA` that only ever substitutes its `title`. Shares
 * `TitleTextCTAView` with `TitleTextCTA` for the same reason the legacy pair does.
 *
 * `title` is a customer-facing template (e.g. `"Welcome, {firstName}!"`, confirmed against the legacy
 * spec's fixtures) resolved against the logged-in customer's first name; anonymous visitors, or a
 * profile that hasn't loaded yet, fall back to the CMS-authored `fallbackTitle` verbatim. The
 * substitution is a plain `String.replace`, not `@eneco/i18n`'s ICU formatter the legacy component uses
 * (`useFormatter`) — `lib:i18n` is not in `lib:cms-components`'s allowed dependencies (`eslint.config.
 * mjs`), the same trade `ContentCard.tsx`'s "step N" comment documents for the same boundary. The CMS
 * template only ever carries the single `{firstName}` token, so a literal replace covers it exactly.
 * The replacement value is passed through a replacer function, not the pattern-string overload — a
 * first name containing a `$&`/`$\``/`$'` sequence is otherwise interpreted as a special replacement
 * token instead of literal text.
 */
const PersonalizedTitleTextCTA: FC = () => {
  const content = useContent<PersonalizedTitleTextCTAContent>();
  const { data } = useCustomerGetCustomerProfile();

  const firstName = data?.person?.firstName;
  const title = firstName ? content.title?.replace('{firstName}', () => firstName) : content.fallbackTitle;

  return <TitleTextCTAView title={title} content={content} />;
};

export default PersonalizedTitleTextCTA;
